import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { useToast } from 'src/hooks/use-toast';
import { Button } from 'src/components/ui/button';
import {
  AlertCircle,
  ArchiveIcon,
  CheckSquare,
  ChevronDown,
  ClockIcon,
  Copy,
  FileText,
  Inbox,
  MapPin,
  Plus,
  SparklesIcon,
  UserPlus,
} from 'lucide-react';
import {
  useOpportunitiesCse,
  useOpportunitiesTa,
  useTaSummary,
  useCseRequirementsSummary,
} from 'src/services/api/queries/opportunityQueries';
import { cn } from 'src/lib/utils';
import TablePagination from 'src/components/common/TablePagination';
import SearchInput from 'src/components/common/SearchInput';
import MetricTile from 'src/components/common/MetricTile';
import DateRangePicker from 'src/components/common/DateRangePicker';
import { useAuth } from 'src/hooks/useAuth';
import TaAssignmentView from './TaAssignmentView';

// `from_date`/`to_date` are YYYY-MM-DD, both ends included — a DateRangePicker
// value's `to` falls back to `from` so an in-progress (single-day) selection
// still filters as that one day rather than being dropped.
const toDateParam = (d) => (d ? format(d, 'yyyy-MM-dd') : undefined);

const CSE_TABS = [
  { key: 'new', label: 'New', icon: SparklesIcon },
  { key: 'active', label: 'Active', icon: ClockIcon },
  { key: 'archive', label: 'Archive', icon: ArchiveIcon },
];

const DEFAULT_PAGE_SIZE = 10;

// Closed tab removed — Active now folds COMPLETED in alongside the two
// still-in-progress statuses, so a finished assignment keeps showing up
// here (with its own terminal-state pill, not a "Submit candidates" button
// — see TaAssignmentView.jsx's statusMetaFor/AssignmentCard) instead of
// needing a dedicated tab of its own.
const getTaStatusFilters = (tab) => {
  if (tab === 'active') return ['PENDING', 'IN_PROGRESS', 'COMPLETED'];
  return undefined;
};

const getTaIsMockValue = (tab, mockFilter) => {
  if (tab === 'archive') return undefined;
  if (mockFilter === 'INTERNAL') return true;
  if (mockFilter === 'CUSTOMER') return false;
  return undefined;
};

// `priority` is the backend's authoritative call (Overdue / Due soon / On
// track) — trust it over re-deriving from days_to_go, which can disagree
// (e.g. days_to_go: 0 doesn't necessarily mean "due today", it can just mean
// "no countdown for this one" while priority already says Overdue).
const getDueVariant = (priority, days) => {
  const p = String(priority || '').toLowerCase();
  if (p === 'overdue')
    return {
      accent: 'var(--ds-danger)',
      pill: {
        label: 'Overdue',
        Icon: AlertCircle,
        className: 'bg-ds-danger-subtle text-ds-danger',
      },
    };
  if (p === 'due soon')
    return {
      accent: 'var(--ds-warning)',
      pill: {
        label: 'Due soon',
        Icon: ClockIcon,
        className: 'bg-ds-warning-subtle text-ds-warning',
      },
    };
  if (days == null) return { accent: 'hsl(var(--primary))', pill: null };
  // Only this branch has a real forward-looking date to swap to on hover —
  // Overdue/Due soon have no deadline date in this response (days_to_go is
  // clamped to 0 once something's overdue), so those pills stay static.
  return {
    accent: 'hsl(var(--primary))',
    pill: {
      label: `${days}d to go`,
      Icon: ClockIcon,
      className: 'bg-muted text-muted-foreground',
    },
    swapLabel: `Due ${format(addDays(new Date(), days), 'dd MMM yyyy')}`,
  };
};

const MetricInner = ({ value, label, empty, isClickable }) => (
  <>
    <span
      className={cn(
        'block font-mono text-[15px] leading-[1.1] tabular-nums',
        empty && 'font-semibold text-foreground-muted',
        !empty && isClickable && 'font-bold text-primary',
        !empty && !isClickable && 'font-bold text-foreground-strong'
      )}
    >
      {value ?? 0}
    </span>
    <span className="block text-[10.5px] text-foreground-muted mt-[2px]">
      {label}
    </span>
  </>
);

const OppMetricCell = ({ value, label, onClick }) => {
  const empty = !value || value === 0;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'px-[16px] py-[7px] text-center min-w-[66px]',
          !empty && 'cursor-pointer hover:bg-brand-bg-subtle transition-colors'
        )}
      >
        <MetricInner
          value={value}
          label={label}
          empty={empty}
          isClickable={!empty}
        />
      </button>
    );
  }
  return (
    <div className="px-[16px] py-[7px] text-center min-w-[66px]">
      <MetricInner value={value} label={label} empty={empty} />
    </div>
  );
};

const fillPct = (required, submitted) =>
  required ? Math.round(((submitted || 0) / required) * 100) : null;

const OppFillCell = ({ required, submitted }) => {
  const pct = fillPct(required, submitted);
  if (pct == null) {
    return (
      <span className="text-[11.5px] font-medium text-foreground-muted">—</span>
    );
  }
  const w = Math.min(100, pct);
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="w-[70px] h-[6px] bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full',
            pct >= 100 ? 'bg-ds-success' : 'bg-primary'
          )}
          style={{ width: `${w}%` }}
        />
      </div>
      <span className="text-[11.5px] font-semibold font-mono text-muted-foreground min-w-[30px] text-right">
        {pct}%
      </span>
    </div>
  );
};

// Per-location Required/Submitted/Offers break-up — same pattern as the TA
// assignment cards' location break-up table.
const OppLocationBreakdown = ({ row }) => {
  const locations = row.locations || [];
  return (
    <div>
      <div className="grid grid-cols-[1.6fr_0.9fr_0.7fr_0.7fr_0.7fr_1fr] gap-x-3 px-3 py-2.5 border-b border-border text-[11px] font-[650] uppercase tracking-[0.06em] text-muted-foreground">
        <span>Location</span>
        <span>Employment type</span>
        <span className="text-right">Required</span>
        <span className="text-right">Submitted</span>
        <span className="text-right">Offers</span>
        <span className="text-right">Fill rate</span>
      </div>
      <div className="divide-y divide-border">
        {locations.map((loc) => (
          <div
            key={loc.requirement_location_id}
            className="grid grid-cols-[1.6fr_0.9fr_0.7fr_0.7fr_0.7fr_1fr] gap-x-3 px-3 py-2.5 text-[13px] items-center"
          >
            <span className="font-medium flex items-center gap-2 text-foreground-strong">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              {loc.location || 'N/A'}
            </span>
            <span className="text-muted-foreground">
              {loc.employment_type || 'N/A'}
            </span>
            <span
              className="text-right font-medium text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {loc.calculated_requirement ?? 0}
            </span>
            <span
              className="text-right font-medium text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {loc.profiles_submitted ?? 0}
            </span>
            <span
              className="text-right font-medium text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {loc.offers_made ?? 0}
            </span>
            <OppFillCell
              required={loc.calculated_requirement}
              submitted={loc.profiles_submitted}
            />
          </div>
        ))}
        {!locations.length && (
          <div className="px-3 py-4 text-[13px] text-center text-muted-foreground">
            No location data available.
          </div>
        )}
        {locations.length > 1 && (
          <div className="grid grid-cols-[1.6fr_0.9fr_0.7fr_0.7fr_0.7fr_1fr] gap-x-3 px-3 py-2.5 text-[13px] items-center border-t border-border font-semibold">
            <span className="text-foreground-strong">Total</span>
            <span className="text-muted-foreground font-normal">
              {locations.length} locations
            </span>
            <span
              className="text-right text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {row.calculated_requirement ?? 0}
            </span>
            <span
              className="text-right text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {row.profiles_submitted ?? 0}
            </span>
            <span
              className="text-right text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {row.offers_made ?? 0}
            </span>
            <OppFillCell
              required={row.calculated_requirement}
              submitted={row.profiles_submitted}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const OppCard = ({
  row,
  navigate,
  toast,
  expanded,
  onToggleExpand,
  isArchiveTab,
}) => {
  const { accent, pill, swapLabel } = getDueVariant(
    row.priority,
    row.days_to_go
  );
  // Per explicit request: an archived requirement's deadline pressure is
  // moot — it's closed either way — so the Overdue/Due soon/Nd-to-go pill
  // (still present in the row data, e.g. a real ARCHIVED item can carry
  // priority: "Overdue") is suppressed on this one tab rather than at the
  // data layer, since the same row shape is reused for every tab.
  const PillIcon = isArchiveTab ? undefined : pill?.Icon;
  const locations = row.locations || [];

  const subtitle = (() => {
    const locPart =
      locations.length === 1
        ? locations[0]?.location
        : `${locations.length} locations`;
    const types = Array.from(
      new Set(locations.map((l) => l.employment_type).filter(Boolean))
    );
    const typePart = types.length <= 2 ? types.join(' & ') : 'Mixed';
    return [locPart, typePart].filter(Boolean).join(' · ');
  })();

  const handleCopyId = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(row.registration_id ?? ''));
      toast({
        message: 'Registration ID copied',
        variant: 'success',
        duration: 1500,
      });
    } catch {
      toast({ message: 'Copy failed', variant: 'destructive', duration: 1500 });
    }
  };

  const handleSubmittedClick = (e) => {
    e.stopPropagation();
    const params = new URLSearchParams({
      status: 'submitted',
      opportunityId: row.requirement_id,
    });
    // Only scope to a single location when the requirement has just one —
    // for a multi-location requirement there's no single right answer.
    if (locations.length === 1 && locations[0]?.requirement_location_id) {
      params.set('requirementLocationId', locations[0].requirement_location_id);
    }
    navigate(`/candidates/list?${params.toString()}`);
  };

  // current_cse_id rides along as router state — confirmed live that
  // GET /requirements/:id/details (what the detail page itself fetches)
  // doesn't carry this field at all, only this list endpoint does. Same
  // "pass context the destination can't otherwise get" technique
  // Preview.jsx's own `state.from` already uses. Falls back to the
  // detail page's own (currently always-empty) field if the backend ever
  // starts sending it there — see OpportunityDetails.jsx's isClaimable.
  const handleCardClick = () =>
    navigate(`/opportunity/${row.requirement_id}`, {
      state: { currentCseId: row.current_cse_id },
    });
  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') handleCardClick();
  };

  return (
    <div className="relative overflow-hidden bg-card border border-border rounded-[10px]">
      <span
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ backgroundColor: accent }}
        aria-hidden="true"
      />
      {row.ta_routing_status === 'ASSIGNED' && (
        <div
          className="group absolute top-0 left-5 bottom-0 h-fit py-[2px] px-[5px] flex items-center justify-center gap-1 text-[8px] font-semibold text-white bg-primary origin-top-left rounded-b-sm"
          aria-hidden="true"
        >
          <UserPlus className="w-2.5 h-2.5 flex-shrink-0" />
          <span className="max-w-0 group-hover:max-w-[90px] overflow-hidden whitespace-nowrap transition-[max-width] duration-300 ease-in-out">
            forwarded to TA
          </span>
          {row.ta_submissions_count > 0 && (
            <span className="flex-shrink-0 tabular-nums">
              {row.ta_submissions_count}
            </span>
          )}
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        className="flex items-center gap-[18px] px-[18px] py-[13px] pl-[24px] transition-shadow hover:shadow-[0_2px_12px_rgba(16,30,54,0.07)] cursor-pointer group"
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[14.5px] font-[650] text-foreground-strong tracking-[-0.01em] leading-snug">
              {row.title}
            </h3>
            {row?.current_cse_id === 0 && (
              <span className="text-[9.5px] font-semibold uppercase tracking-[0.03em] px-[6px] py-[1px] rounded-[4px] bg-brand-bg-subtle text-primary">
                NEW
              </span>
            )}
          </div>

          <p className="text-[12px] text-foreground-muted mt-[2px]">
            {subtitle}
          </p>

          <div className="flex items-center gap-[7px] mt-[7px] text-[11.5px] flex-wrap">
            <span className="flex items-center gap-[5px]">
              <span className="font-mono font-semibold text-muted-foreground">
                {row.registration_id}
              </span>
              <button
                type="button"
                onClick={handleCopyId}
                aria-label="Copy registration ID"
                className="opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 text-foreground-muted hover:text-primary"
              >
                <Copy className="w-[12px] h-[12px]" />
              </button>
            </span>
            <span
              className="w-[3px] h-[3px] rounded-full bg-border"
              aria-hidden="true"
            />
            <span className="text-muted-foreground font-medium">
              {row.organization_name}
            </span>
            {row.created_at && (
              <>
                <span
                  className="w-[3px] h-[3px] rounded-full bg-border"
                  aria-hidden="true"
                />
                <span className="text-foreground-muted">
                  Posted {format(new Date(row.created_at), 'dd MMM yyyy')}
                </span>
              </>
            )}
            {isArchiveTab && row.archived_at && (
              <>
                <span
                  className="w-[3px] h-[3px] rounded-full bg-border"
                  aria-hidden="true"
                />
                <span className="text-foreground-muted">
                  Archived {format(new Date(row.archived_at), 'dd MMM yyyy')}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-[8px] flex-shrink-0">
          {pill && PillIcon && (
            <span
              className={cn(
                'inline-flex items-center gap-[5px] px-[9px] py-[3px] rounded-[6px] text-[11.5px] font-semibold whitespace-nowrap',
                pill.className
              )}
            >
              <PillIcon className="w-[12px] h-[12px]" strokeWidth={2} />
              {swapLabel ? (
                <>
                  <span className="inline-grid">
                    <span className="[grid-area:1/1] transition-opacity duration-150 group-hover:opacity-0 group-focus-within:opacity-0">
                      {pill.label}
                    </span>
                    <span
                      aria-hidden="true"
                      className="[grid-area:1/1] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
                    >
                      {swapLabel}
                    </span>
                  </span>
                  <span className="sr-only"> — {swapLabel}</span>
                </>
              ) : (
                pill.label
              )}
            </span>
          )}

          <div className="flex border border-borderColorSecondary rounded-[9px] overflow-hidden divide-x divide-borderColorSecondary">
            <OppMetricCell
              value={row.calculated_requirement}
              label="Required"
            />
            <OppMetricCell
              value={row.profiles_submitted}
              label="Submitted"
              onClick={
                row.profiles_submitted > 0 ? handleSubmittedClick : undefined
              }
            />
            <OppMetricCell value={row.offers_made} label="Offers" />
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(row.requirement_id);
          }}
          aria-expanded={expanded}
          aria-label={`Location breakdown for ${row.title || 'opportunity'}`}
          className="h-8 w-8 p-0 text-muted-foreground hover:text-muted-foreground hover:bg-muted flex-shrink-0"
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              expanded && 'rotate-180'
            )}
          />
        </Button>
      </div>

      {expanded && (
        <div
          className="border-t border-border px-[18px] py-[14px]"
          style={{ backgroundColor: 'var(--muted)' }}
        >
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2.5">
            Location break-up
          </p>
          <OppLocationBreakdown row={row} />
        </div>
      )}
    </div>
  );
};

const OppListSkeleton = () => (
  <div className="flex flex-col gap-[10px]">
    {['sk-0', 'sk-1', 'sk-2', 'sk-3', 'sk-4'].map((id) => (
      <div
        key={id}
        className="relative bg-card border border-border rounded-[10px] px-[18px] py-[13px] pl-[24px] flex items-center gap-[18px] animate-pulse"
      >
        <span className="absolute left-[10px] top-[14px] bottom-[14px] w-[3px] rounded-[3px] bg-border" />
        <div className="flex-1 min-w-0 space-y-[8px]">
          <div className="h-[18px] bg-muted rounded-[4px] w-2/3" />
          <div className="h-[13px] bg-muted rounded-[4px] w-1/3" />
          <div className="h-[13px] bg-muted rounded-[4px] w-1/4" />
        </div>
        <div className="flex flex-col items-end gap-[8px]">
          <div className="h-[24px] bg-muted rounded-[6px] w-[80px]" />
          <div className="h-[42px] bg-muted rounded-[9px] w-[198px]" />
        </div>
      </div>
    ))}
  </div>
);

const OppEmptyState = ({ searchQuery, activeTab }) => (
  <div className="border border-border rounded-xl bg-card">
    <div className="flex flex-col items-center justify-center py-[50px] px-5 text-center">
      <div className="w-[46px] h-[46px] rounded-xl bg-brand-bg-subtle text-primary inline-flex items-center justify-center mb-3">
        <FileText className="w-[22px] h-[22px]" strokeWidth={1.8} />
      </div>
      <p className="text-[15px] font-[650] text-foreground-strong mb-1">
        Nothing here yet
      </p>
      <p className="text-[13px] text-foreground-muted max-w-[320px]">
        {searchQuery
          ? 'No opportunities match your search.'
          : `There are no ${activeTab} opportunities right now.`}
      </p>
    </div>
  </div>
);

function Opportunities() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getUserInfo } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = getUserInfo();
  const isCse = user?.roles?.includes('ahedge_cse');
  const isTa = user?.roles?.includes('ahedge_ta');

  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState(undefined);
  const [expandedOppId, setExpandedOppId] = useState(null);
  const toggleOppExpand = (id) => {
    if (id === undefined || id === null) return;
    setExpandedOppId((prev) => (prev === id ? null : id));
  };

  const [taActiveTab, setTaActiveTab] = useState('active');
  const [taSearchQuery, setTaSearchQuery] = useState('');
  const [debouncedTaSearchQuery, setDebouncedTaSearchQuery] = useState('');
  const [taMockFilter, setTaMockFilter] = useState('ALL');
  const [taDateRange, setTaDateRange] = useState(undefined);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  React.useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedTaSearchQuery(taSearchQuery),
      500
    );
    return () => clearTimeout(timer);
  }, [taSearchQuery]);

  const getStatusFromTab = (tab) =>
    ({ new: 'NEW', active: 'ACTIVE', archive: 'ARCHIVE' })[tab] || 'ACTIVE';

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  const getCseStatus = () => {
    if (isCse && activeTab === 'archive') return 'ARCHIVE';
    if (isCse) return 'ACTIVE';
    return getStatusFromTab(activeTab);
  };

  const {
    data: cseData,
    isLoading: isCseLoading,
    error: cseError,
  } = useOpportunitiesCse({
    status: getCseStatus(),
    page: currentPage,
    pageSize: currentPageSize,
    activeTab: isCse ? activeTab : undefined,
    search: isCse && debouncedSearchQuery ? debouncedSearchQuery : undefined,
    fromDate: toDateParam(dateRange?.from),
    toDate: toDateParam(dateRange?.to || dateRange?.from),
  });

  const {
    data: taData,
    isLoading: isTaLoading,
    error: taError,
  } = useOpportunitiesTa({
    page: currentPage,
    pageSize: currentPageSize,
    activeTab: taActiveTab,
    statusFilters: getTaStatusFilters(taActiveTab),
    isMock: getTaIsMockValue(taActiveTab, taMockFilter),
    isArchive: taActiveTab === 'archive' ? true : undefined,
    search: isTa && debouncedTaSearchQuery ? debouncedTaSearchQuery : undefined,
    fromDate: toDateParam(taDateRange?.from),
    toDate: toDateParam(taDateRange?.to || taDateRange?.from),
  });

  const {
    data: taSummaryData,
    isLoading: isTaSummaryLoading,
    error: taSummaryError,
  } = useTaSummary();

  const { data: cseSummaryData } = useCseRequirementsSummary();

  let opportunitiesData;
  let isLoading;
  let error;

  if (isCse) {
    opportunitiesData = cseData;
    isLoading = isCseLoading;
    error = cseError;
  } else {
    opportunitiesData = taData;
    isLoading = isTaLoading;
    error = taError || taSummaryError;
  }

  React.useEffect(() => {
    if (error) {
      toast({
        message: 'Failed to load opportunities. Please refresh the page.',
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) =>
    updateUrlParams({ page: parseInt(newPage, 10) });

  const handlePageSizeChange = (newPageSize) =>
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });

  const handleTabChange = (value) => {
    setActiveTab(value);
    updateUrlParams({ page: 1 });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!searchParams.has('pageSize'))
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const prevDebouncedSearchRef = React.useRef(debouncedSearchQuery);
  const prevDebouncedTaSearchRef = React.useRef(debouncedTaSearchQuery);

  React.useEffect(() => {
    if (
      isCse &&
      prevDebouncedSearchRef.current !== debouncedSearchQuery &&
      currentPage !== 1
    ) {
      prevDebouncedSearchRef.current = debouncedSearchQuery;
      updateUrlParams({ page: 1 });
    }
  }, [debouncedSearchQuery, isCse, currentPage]);

  React.useEffect(() => {
    if (
      isTa &&
      prevDebouncedTaSearchRef.current !== debouncedTaSearchQuery &&
      currentPage !== 1
    ) {
      prevDebouncedTaSearchRef.current = debouncedTaSearchQuery;
      updateUrlParams({ page: 1 });
    }
  }, [debouncedTaSearchQuery, isTa, currentPage]);

  const allRows = opportunitiesData?.data?.items || [];
  const totalPages = opportunitiesData?.data?.pagination?.total_pages || 1;
  const totalCount = opportunitiesData?.data?.pagination?.total;

  const apiMetrics = opportunitiesData?.data?.metrics ?? {};
  const TAB_METRIC_KEY = {
    new: 'new',
    active: 'active',
    archive: 'archived',
  };
  const getTabCount = (tabKey) => apiMetrics[TAB_METRIC_KEY[tabKey]];

  const cseRequirements = cseSummaryData?.data?.requirements;
  const cseSubmissions = cseSummaryData?.data?.submissions;
  const cseSubmitted = cseSubmissions?.submitted_count ?? 0;
  const cseRequested = cseSubmissions?.total_assignments_count ?? 0;
  const cseSubmittedPct = cseRequested
    ? Math.round((cseSubmitted / cseRequested) * 100)
    : 0;

  if (isTa) {
    return (
      <div>
        <PageHeader
          title="Opportunities"
          actions={
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => navigate('/source-candidates/create')}
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
              Create Requirement
            </Button>
          }
        />
        <TaAssignmentView
          taData={taData}
          taSummaryData={taSummaryData}
          isLoading={isLoading}
          isTaSummaryLoading={isTaSummaryLoading}
          currentPage={currentPage}
          currentPageSize={currentPageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          activeTab={taActiveTab}
          onTabChange={(tab) => {
            setTaActiveTab(tab);
            updateUrlParams({ page: 1 });
          }}
          mockFilter={taMockFilter}
          onMockFilterChange={(value) => {
            setTaMockFilter(value);
            updateUrlParams({ page: 1 });
          }}
          searchQuery={taSearchQuery}
          onSearchChange={setTaSearchQuery}
          dateRange={taDateRange}
          onDateRangeChange={(range) => {
            setTaDateRange(range);
            updateUrlParams({ page: 1 });
          }}
        />
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Opportunities" />
      <PageContainer>
        {/* Page header */}
        <header className="mb-[22px]">
          <h1 className="text-[22px] font-bold tracking-[-0.04em] text-foreground-strong mb-1">
            Opportunities
          </h1>
          <p className="text-[13.5px] text-muted-foreground leading-[1.55]">
            Role requirements submitted by partner banks and fintechs — review,
            source candidates, and track delivery against each request.
          </p>
        </header>

        {/* Summary metric strip */}
        <div className="grid grid-cols-5 gap-[14px] mb-[18px]">
          <MetricTile
            icon={Inbox}
            iconBg="hsl(var(--brand-bg-subtle))"
            iconColor="hsl(var(--primary))"
            value={cseRequirements?.active ?? 0}
            label="Active requirements"
          />
          <MetricTile
            icon={AlertCircle}
            iconBg="var(--ds-danger-subtle)"
            iconColor="var(--ds-danger)"
            value={cseRequirements?.overdue ?? 0}
            label="Overdue"
          />
          <MetricTile
            icon={ClockIcon}
            iconBg="var(--ds-warning-subtle)"
            iconColor="var(--ds-warning)"
            value={cseRequirements?.due_soon ?? 0}
            label="Due soon"
          />
          <MetricTile
            wide
            icon={CheckSquare}
            iconBg="var(--ds-success-subtle)"
            iconColor="var(--ds-success)"
            value={cseSubmitted}
            ofValue={cseRequested}
            label="Candidates submitted"
            sub={`${cseSubmittedPct}% of requested`}
            barPct={cseSubmittedPct}
          />
        </div>

        {/* Tab bar */}
        <div className="flex items-center mb-[18px]">
          {CSE_TABS.map(({ key, label, icon: Icon }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleTabChange(key)}
                className={cn(
                  'flex items-center gap-2 px-[18px] py-[10px] text-[13px] border-b-2 -mb-px transition-colors',
                  isActive
                    ? 'font-semibold border-primary text-primary'
                    : 'font-medium border-transparent text-muted-foreground hover:text-foreground-strong'
                )}
              >
                <Icon className="w-[14px] h-[14px]" />
                {label}
                {getTabCount(key) != null && (
                  <span
                    className={cn(
                      'text-[11px] font-semibold px-[7px] py-[1px] rounded-full',
                      isActive
                        ? 'bg-brand-bg-subtle text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {getTabCount(key)}
                  </span>
                )}
              </button>
            );
          })}

          <div className="ml-auto flex items-center gap-2 pb-1">
            <DateRangePicker
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                updateUrlParams({ page: 1 });
              }}
              className="w-[220px]"
            />
            <SearchInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder="Search role, Reg ID..."
              className="w-[220px]"
              inputClassName="py-1.5"
            />
          </div>
        </div>

        {/* Card list */}
        {isLoading && <OppListSkeleton />}
        {!isLoading && allRows.length === 0 && (
          <OppEmptyState searchQuery={searchQuery} activeTab={activeTab} />
        )}
        {!isLoading && allRows.length > 0 && (
          <div className="flex flex-col gap-[10px]">
            {allRows.map((row) => (
              <OppCard
                key={row.requirement_id}
                row={row}
                navigate={navigate}
                toast={toast}
                expanded={expandedOppId === row.requirement_id}
                onToggleExpand={toggleOppExpand}
                isArchiveTab={activeTab === 'archive'}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && allRows.length > 0 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={currentPageSize}
            totalItems={totalCount ?? allRows.length}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="opportunities"
            className="mt-4"
          />
        )}
      </PageContainer>
    </>
  );
}

export default Opportunities;
